import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  PhoneDisabled as PhoneDisabledIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { formatDateTimeBR } from '../utils/dateUtils';

interface DesignerInfo {
  id: string;
  name: string;
  email: string;
  status: 'GOOD_FLOW' | 'NORMAL' | 'BUSY' | 'SLOW';
  lastUpdate: string;
  ordersInProgress: number;
  ordersFinalized: number;
  ordersSemRetorno: number;
  totalArtsMonth: number;
  totalOrders: number;
}

const DesignerService: React.FC = () => {
  const navigate = useNavigate();
  const [designers, setDesigners] = useState<DesignerInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDesigners = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getDesignerServiceInfo();
      setDesigners(data);
    } catch (err: any) {
      console.error('Erro ao carregar designers:', err);
      setError('Erro ao carregar informações dos designers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDesigners();
    const interval = setInterval(loadDesigners, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'GOOD_FLOW': return 'success';
      case 'NORMAL': return 'info';
      case 'BUSY': return 'warning';
      case 'SLOW': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'GOOD_FLOW': return 'Suave';
      case 'NORMAL': return 'Fluxo Bom';
      case 'BUSY': return 'Correria';
      case 'SLOW': return 'Sobrecarregado';
      default: return status;
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case 'GOOD_FLOW': return 'Poucos pedidos, pode receber mais trabalho tranquilamente';
      case 'NORMAL': return 'Ritmo normal, ainda pode receber novos pedidos';
      case 'BUSY': return 'Muitos pedidos, cuidado ao encaminhar novos trabalhos';
      case 'SLOW': return 'Volume muito alto, evitar novos pedidos';
      default: return '';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'GOOD_FLOW': return '\u{1F60A}';
      case 'NORMAL': return '\u{1F600}';
      case 'BUSY': return '\u{26A1}';
      case 'SLOW': return '\u{1F630}';
      default: return '\u{2753}';
    }
  };

  const handleViewDesignerOrders = (designerId: string, designerName?: string) => {
    const nameParam = designerName ? `&designerName=${encodeURIComponent(designerName)}` : '';
    navigate(`/designer-interface?designerId=${designerId}${nameParam}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={loadDesigners} startIcon={<RefreshIcon />}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  const totalInProgress = designers.reduce((sum, d) => sum + d.ordersInProgress, 0);
  const totalSemRetorno = designers.reduce((sum, d) => sum + (d.ordersSemRetorno || 0), 0);
  const totalArtsMonth = designers.reduce((sum, d) => sum + (d.totalArtsMonth || 0), 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon sx={{ color: '#6366f1' }} />
            Atendimento Arte
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Acompanhe o status e o fluxo de trabalho de cada designer
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadDesigners} disabled={loading}>
          Atualizar
        </Button>
      </Box>

      {/* Cards de Resumo */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Total de Designers</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>{designers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Em Andamento</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>{totalInProgress}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%', borderLeft: '4px solid #ef4444' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PhoneDisabledIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sem Retorno</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>{totalSemRetorno}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', height: '100%', borderLeft: '4px solid #8b5cf6' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PaletteIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Total Artes/Mês</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>{totalArtsMonth}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Designers */}
      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Status dos Designers</Typography>

          {designers.length === 0 ? (
            <Alert severity="info">Nenhum designer encontrado</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Designer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Em Andamento</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Finalizados</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Sem Retorno</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Artes/Mês</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Última Atualização</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {designers.map((designer) => (
                    <TableRow key={designer.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{designer.name}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{designer.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={getStatusDescription(designer.status)} arrow>
                          <Chip
                            icon={<span style={{ fontSize: '16px' }}>{getStatusIcon(designer.status)}</span>}
                            label={getStatusLabel(designer.status)}
                            color={getStatusColor(designer.status) as any}
                            size="small"
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip icon={<PendingIcon />} label={designer.ordersInProgress} color="warning" size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip icon={<CheckCircleIcon />} label={designer.ordersFinalized} color="success" size="small" />
                      </TableCell>
                      <TableCell align="center">
                        {(designer.ordersSemRetorno || 0) > 0 ? (
                          <Chip
                            icon={<PhoneDisabledIcon />}
                            label={designer.ordersSemRetorno}
                            size="small"
                            sx={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<PaletteIcon />}
                          label={designer.totalArtsMonth || 0}
                          size="small"
                          sx={{ backgroundColor: '#f3e8ff', color: '#6b21a8', fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{designer.totalOrders}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDateTimeBR(designer.lastUpdate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Pedidos do Designer">
                          <IconButton size="small" color="primary" onClick={() => handleViewDesignerOrders(designer.id, designer.name)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DesignerService;
